import applications from '@/config/applications'
import brands from '@/config/brands'
import urls from '@/config/urls'
import { getFlow, getHint, getLocale, getStaticRootUrl } from '@ua-okta-ui/lib/utils'

import { WidgetOptions } from '@okta/okta-signin-widget'
import TranslationsEN from '@ua-okta-ui/config/translations/okta/en'

import logger from '@ua-okta-ui/lib/logger'
import {
	ApplicationConfig,
	BrandConfig,
	Locale,
	StorefrontEnvironment,
	TermsAndConditions,
	TermsAndConditionsContext,
} from '@ua-okta-ui/types'
import { Flows } from '@ua-okta-ui/types/enum'
import { STOREFRONT_ENVIRONMENTS, TermsAndConditionsContexts } from '@ua-okta-ui/types/constants'

import { EventContext } from '@okta/okta-signin-widget'
import {
	DEFAULT_TERMS_AND_CONDITIONS,
	TermsAndConditionsConfig,
} from '@ua-okta-ui/config/translations/terms-and-conditions'
import { ContextViews } from '@ua-okta-ui/types/enum'

export function createCustomButtons(application?: ApplicationConfig): WidgetOptions['customButtons'] {
	const buttons: WidgetOptions['customButtons'] = []

	// If not configured to hide and the referrer is not itself
	if (
		(typeof application?.hideContinueAsGuest === 'undefined' || !application.hideContinueAsGuest) &&
		// if the referrer is itself, it's likely the Fastly challenge; otherwise, hide the button if the origins match
		(document.referrer === document.location.href || !document.referrer.startsWith(document.location.origin))
	) {
		buttons.push({
			i18nKey: 'customButton.title.continueAsGuest',
			className: 'btn-customContinueAsGuest',
			click: () => {
				window.location.href = 'javascript:history.back()'
			},
		})
	}

	return buttons
}

export function getApplication(): ApplicationConfig {
	const requestContext = OktaUtil?.getRequestContext()
	const id = requestContext?.app?.value?.id
	const application = Object.values(globalThis.uaOkta.applications).find((a) => a.id === id)

	if (!application) {
		logger.error('Could not determine application. Defaulting to seabiscuit-na')
	}

	return application ?? globalThis.uaOkta.applications['seabiscuit-na']
}

export function getBrand(): BrandConfig {
	const { brandName } = oktaData.signIn
	const brand = globalThis.uaOkta.brands[brandName]

	if (!brand) {
		logger.error('Could not determine brand. Defaulting to us-web')
	}

	// TODO: brand environment default
	return brand ?? globalThis.uaOkta.brands['us-web']
}

export const getAssetsRewite: (assetPath: string) => string = (assetPath: string) =>
	// TODO: do we just use the underscore?
	assetPath.replace(/(\w{2})_(\w{2})\.json/g, '$1-$2.json').toLowerCase()

export function getStorefrontEnv(): StorefrontEnvironment {
	const params = new URLSearchParams(window.location.search)
	const envParam = params.get('env')
	const env = envParam?.toLowerCase()
	return !!env && STOREFRONT_ENVIRONMENTS.includes(env as StorefrontEnvironment)
		? (env as StorefrontEnvironment)
		: 'default'
}

export async function getWidgetOptions({
	application,
	locale,
}: {
	application: ApplicationConfig
	brand: BrandConfig
	locale: Locale
}): Promise<WidgetOptions & { backToSignInLink?: string | null }> {
	// The following feature broke the default redirect on expired forgot-password urls
	// - https://github.com/okta/okta-signin-widget/pull/3765
	// requiring some value to be configured for the link to function
	const backToSignInLink = ((): string | undefined => {
		if (document.location.pathname.startsWith('/email/verify/')) {
			return document.location.origin
		}
		return undefined
	})()

	return {
		...((await OktaUtil?.getSignInWidgetConfig()) ?? {}),
		assets: {
			baseUrl: `${uaOkta.urls.assets}/${uaOkta.version}`,
			rewrite: getAssetsRewite,
		},
		backToSignInLink,
		// Obfuscate brandName so it's not rendered within translations
		brandName: undefined,
		customButtons: createCustomButtons(application),
		// This needs to initialize as LOGIN; otherwise, cookies won't be POSTed when swapping to register
		flow: Flows.LOGIN,
		i18n: {
			...(application.translationOverrides ?? {}),
			en: TranslationsEN.login,
		},
		language: locale,
		registration: {
			preSubmit(postData, onSuccess) {
				onSuccess(trimRegistrationPostData(postData))
			},
			postSubmit(response, onSuccess) {
				logger.info('registration success', { response })
				onSuccess(response)
			},
		},
	}
}

export function trimRegistrationPostData<T>(postData: unknown): T {
	if (
		!!postData &&
		typeof postData === 'object' &&
		'userProfile' in postData &&
		!!postData.userProfile &&
		typeof postData.userProfile === 'object' &&
		'email' in postData.userProfile &&
		!!postData.userProfile.email &&
		typeof postData.userProfile.email === 'string'
	) {
		return {
			...postData,
			userProfile: {
				...postData.userProfile,
				email: postData.userProfile.email.trim(),
			},
		} as T
	}
	return postData as T
}

enum LocalStorageKeys {
	UA_OKTA_VERSION = 'ua-okta-version',
	WIDGET_LANGUAGE_CACHE = 'osw.languages',
}

function cloneNode<T extends Node>(node: T) {
	return <T>node.cloneNode()
}

export function configureLocalStorage(): void {
	const storageVersion = window.localStorage.getItem(LocalStorageKeys.UA_OKTA_VERSION)
	if (!storageVersion || storageVersion !== uaOkta.version) {
		window.localStorage.removeItem(LocalStorageKeys.WIDGET_LANGUAGE_CACHE)
		window.localStorage.setItem(LocalStorageKeys.UA_OKTA_VERSION, uaOkta.version)
	}
}

export function createTermsAndConditionsFooter(terms: string): HTMLElement {
	// Create a new section to put new terms and conditions on signon page and create account pages
	const divTaCFooter = document.createElement('div')
	const spanTacFooter = document.createElement('span')
	// Add the appropriate classes to above elements
	divTaCFooter.classList.add('footer-element')
	spanTacFooter.classList.add('login-disclaimer')

	spanTacFooter.innerHTML = terms
	// Add Span to terms and conditions footer
	divTaCFooter.append(spanTacFooter)

	// Return the Div which contains a span of Terms and Conditions
	return divTaCFooter
}

// TODO: add tests
export function findFooter(): Element | undefined {
	const generateSelector = (form: ContextViews, button: 'back' | 'enroll' | 'save' | 'cancel'): string =>
		`div[data-form-context="${form}"] div[class^="MuiBox"]:has(form) div[class^="MuiBox-root"]:has(button[data-se="${button}"])`

	return (
		[
			generateSelector(ContextViews.LOGIN, 'save'),
			generateSelector(ContextViews.LOGIN, 'cancel'),
			generateSelector(ContextViews.REGISTER, 'back'),
			generateSelector(ContextViews.REGISTER, 'enroll'),
			generateSelector(ContextViews.RESET_PASSWORD_1, 'save'),
			generateSelector(ContextViews.RESET_PASSWORD_2, 'save'),
		]
			.map((selector) => document.querySelector(selector))
			.find(Boolean) ?? undefined
	)
}

export function getGlobalUaOkta(): typeof uaOkta {
	return {
		...(globalThis?.uaOkta ?? {}),
		applications,
		brands,
		// TODO: configure preview?
		environment: 'production',
		flow: getFlow(),
		hint: getHint(),
		locale: getLocale(),
		staticRootUrl: getStaticRootUrl() ?? '',
		urls,
		// This is replaced at build time with tsup. see ~/tsup.config.js
		version: process.env['TSUP_REPLACE_VERSION'] ?? '',
	}
}

// TODO: add tests
export function getTermsAndConditions({
	brand,
	fallback,
	locale,
}: {
	brand: BrandConfig
	fallback?: Locale
	locale: string
}): TermsAndConditions {
	return (
		[locale, fallback, brand?.locales?.[0]]
			.filter((l): l is Locale => !!l)
			.map((l) => TermsAndConditionsConfig.overrides[brand.id]?.[l] ?? TermsAndConditionsConfig.regions[l])
			.find((tac) => !!tac) ?? DEFAULT_TERMS_AND_CONDITIONS
	)
}

// TODO: remove casting
export function getTermsAndConditionsContext(context: ContextViews): TermsAndConditionsContext | undefined {
	if (TermsAndConditionsContexts.includes(context as TermsAndConditionsContext)) {
		return context as TermsAndConditionsContext
	}
	return undefined
}

// TODO: add tests
export function getTermsAndConditionsForContext({
	context,
	termsAndConditions,
}: {
	context: TermsAndConditionsContext
	termsAndConditions: TermsAndConditions
}): string | undefined {
	const termsRaw = [termsAndConditions[context], termsAndConditions[ContextViews.LOGIN]]
		.filter(Boolean)
		.find((terms) => !!terms)

	return termsRaw?.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank">$1</a>')
}

export function loadTranslationFromStorage({ locale, logger }: { locale: string; logger: (msg: string) => void }) {
	try {
		return (
			JSON.parse(window.localStorage.getItem(LocalStorageKeys.WIDGET_LANGUAGE_CACHE) ?? '{}')?.[locale]?.login ?? {}
		)
	} catch (e) {
		const error = e as Error
		logger(`Could not initialize translations: ${error.message}`)
		return {}
	}
}

export function mutateButton({
	onclick,
	selector,
	text,
}: {
	onclick: (e: MouseEvent) => void
	selector: (element: string) => string
	text?: string
}): void {
	const button = [
		...document.querySelectorAll<HTMLButtonElement | HTMLLinkElement>(['a', 'button'].map(selector).join(',')),
	]?.[0]

	if (!!button) {
		// Clone elements, so the react context is lost
		const clonedButton = cloneNode(button)

		clonedButton.innerHTML = text ?? button.innerHTML
		clonedButton.onclick = onclick

		button.replaceWith(clonedButton)
	}
}

export function mutateElement({
	element: observedElement,
	selector,
	condition = () => true,
	mutate,
}: {
	element: Element
	selector: string
	condition?: () => boolean
	mutate: () => void
}): void {
	// The afterRender event isn't reliably triggered after the dom has been updated. Because of this
	// if the inital pass fails, we'll fallback to a `MutationObserver` on the root element the Okta
	// React app initialized on.
	//
	// Execute immediately - applicable for refreshes
	mutate()

	// When deeplinking (with the sig-sci workaround), there's a few redraws. The final typically happens
	// after the afterRender callback is triggered. Because of this, we'll add a mutation observer to
	// capture that final redraw, and perform the mutation only if the cancel (Back to login) button is found.
	if (condition()) {
		const observer = new MutationObserver((mutations, obs) => {
			const element = mutations
				.filter((m) => m.type === 'childList')
				.map((m) => m.target as Element)
				.map((e) => e.querySelector(selector))
				.find((h) => !!h)

			if (element) {
				// Disconnect to prevent further mutations
				obs.disconnect()
				mutate()
			}
		})

		observer.observe(observedElement, { childList: true, subtree: true })
	}
}

// TODO: add tests
export function parseContext(context: EventContext['formName']): ContextViews | undefined {
	return Object.values<string>(ContextViews).includes(context ?? '') ? (context as ContextViews) : undefined
}

export function getStorefrontUrl({ storefrontUrls: urls }: BrandConfig): string | undefined {
	const env = getStorefrontEnv()
	return env && urls ? urls[env] : undefined
}
